import { useLingui } from '@lingui/react/macro';
import { Calendar, CalendarDays, CheckCircle, Target, TrendingUp, Trophy, Users } from 'lucide-react';
import React from 'react';
import { SkeletonPreview } from 'ud-ui/components/skeleton';
import { TournamentAnalyticsSummary } from 'modules/analytics/domain/interfaces/tournament-analytics';
import KpiCard from './kpi-card';
import { KpiGridSkeleton } from './kpi-card-skeleton';
import { KPI_LABELS } from './kpi-labels';
import { KPI_GRID_CLASS } from './kpi-layout';

interface KpiGridProps {
  summary: TournamentAnalyticsSummary | undefined;
  loading: boolean;
}

const ICON_SIZE = 22;

const KpiGrid = React.memo(({ summary, loading }: KpiGridProps) => {
  const { t } = useLingui();

  if (loading || !summary) {
    return <KpiGridSkeleton />;
  }

  const playedRatio = summary.matchesCount > 0 ? summary.playedMatchesCount / summary.matchesCount : 0;

  return (
    <SkeletonPreview skeleton={<KpiGridSkeleton />}>
      <div className={KPI_GRID_CLASS}>
        <KpiCard
          icon={<Trophy size={ICON_SIZE} />}
          value={summary.championshipsCount}
          label={t(KPI_LABELS.championships)}
          tone="gold"
        />
        <KpiCard icon={<Calendar size={ICON_SIZE} />} value={summary.matchesCount} label={t(KPI_LABELS.matches)} tone="blue" />
        <KpiCard
          icon={<CheckCircle size={ICON_SIZE} />}
          value={summary.playedMatchesCount}
          label={t(KPI_LABELS.playedMatches)}
          tone="green"
          progress={playedRatio}
          progressHint={`${summary.playedMatchesCount} / ${summary.matchesCount}`}
        />
        <KpiCard icon={<Users size={ICON_SIZE} />} value={summary.uniqueTeamsCount} label={t(KPI_LABELS.uniqueTeams)} tone="blue" />
        <KpiCard icon={<Target size={ICON_SIZE} />} value={summary.totalGoals} label={t(KPI_LABELS.totalGoals)} tone="amber" />
        <KpiCard
          icon={<TrendingUp size={ICON_SIZE} />}
          value={summary.averageGoalsPerMatch}
          label={t(KPI_LABELS.averageGoals)}
          tone="amber"
        />
        <KpiCard icon={<CalendarDays size={ICON_SIZE} />} value={summary.gameDaysCount} label={t(KPI_LABELS.gameDays)} tone="gold" />
      </div>
    </SkeletonPreview>
  );
});

export default KpiGrid;
